import React from "react";
import axios from "axios";
import {
  MMB_HISTORY_URL,
  MMB_HISTORY,
  ENERGY,
  TRADING_SERVER_URL,
  ENERGY_COLUMNS,
  ENERGY_SYMBOL,
} from "./constants.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./mmb.css";
import isDuringMarketTime from "./utils.js";
import {
  convertDateToEST,
  convertDateToESTString,
  bufferToBase64Image,
  getMostRecentMarketDate,
  getHeaders,
  isMarketOpenOnDate,
  getTimezoneDiffWithEST,
} from "./utils.js";
import Table from "./table.js";
import "./table.css";
import "./history.css";
import EnlargeableImage from "./enlargeable-image.js";
import FitController, {
  measureHeight,
  measureTop,
  MIN_IMAGE_HEIGHT,
  FALLBACK_HEADER_HEIGHT,
} from "./fit-controller.js";

const formatTableViewOptions = {
  hour: "numeric",
  minute: "numeric",
  hour12: false,
  timeZone: "America/New_York",
};

export default class History extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedDate: props.selectedDate || getMostRecentMarketDate(props.marketCalendar),
      onDateChangeCallback: props.onDateChangeCallback,
      isEnergy: props.isEnergy,
      isLive: props.isLive,
      selectedEnergySymbol: props.isEnergy
        ? props.energySymbol || ENERGY_SYMBOL.SPY
        : null,
      // When the symbol is pinned by the parent (e.g. the SPY/QQQ pair shown
      // side by side) the toggle is replaced by a plain label.
      pinnedEnergySymbol: props.isEnergy && !!props.energySymbol,
      symbols: props.isEnergy ? ENERGY : MMB_HISTORY,
      url: props.isEnergy ? TRADING_SERVER_URL : MMB_HISTORY_URL,
      imgToDisplay: [],
      marketCalendar: props.marketCalendar,
    };

    this.containerRef = React.createRef();
    this.headerRef = React.createRef();
    this.fit = new FitController({
      getSignature: () => this.getFitSignature(),
      applyFit: () => this.applyFit(),
    });

    // Several energy tables can live on the same page, so the scroll container
    // needs an id of its own.
    this.tableId = props.isEnergy
      ? `energy-table-${props.energySymbol || "default"}`
      : "energy-table";

    // Energy is table-only: the server no longer keeps a png for a fully parsed
    // reading, so there is nothing to page through as images.
    if (props.isEnergy) {
      this.crawlEnergyTableview().then(() => this.init());
    } else {
      this.crawlMMB()
        .then(() => this.resizeMMB())
        .then(() => this.init());
    }
  }

  componentDidMount() {
    // The first fit can run before the refs exist, in which case it used
    // fallback measurements; this check redoes it with the real ones.
    this.fit.reset();
    this.fit.check();
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedDate !== prevProps.selectedDate) {
      this.onDateChangeInternal(this.props.selectedDate);
    }

    if (!this.state.isEnergy) {
      this.fit.check();
    }
  }

  init() {
    if (this.isUnmounted) {
      return;
    }

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("touchstart", this.handleTouchStart);
    window.addEventListener("touchend", this.handleTouchEnd);

    if (this.state.isLive) {
      this.refreshTimer = setInterval(() => {
        if (!isDuringMarketTime(this.state.marketCalendar)) {
          return;
        }

        this.state.selectedDate = new Date();
        if (this.state.isEnergy) {
          this.crawlEnergyTableview();
        } else {
          this.crawlMMB();
        }
      }, 1000 * 60);
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.fit.cancel();
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("touchstart", this.handleTouchStart);
    window.removeEventListener("touchend", this.handleTouchEnd);
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.touchTimer) {
      clearInterval(this.touchTimer);
      this.touchTimer = null;
    }
  }

  handleResize = () => {
    if (this.state.isEnergy) {
      return;
    }

    this.resizeMMB();
  };

  handleKeyDown = (event) => {
    if (this.state.isEnergy) {
      return;
    }

    if (event.code == "ArrowRight") {
      this.moveRight();
    } else if (event.code == "ArrowLeft") {
      this.moveLeft();
    }
  };

  handleTouchStart = (event) => {
    if (this.state.isEnergy) {
      return;
    }

    const posX = event.touches[0].clientX;
    const posY = event.touches[0].clientY;

    if (posY <= window.innerHeight * 0.2) {
      return;
    }

    if (posX >= window.innerWidth * 0.9 && !this.touchTimer) {
      this.moveRight();
      this.touchTimer = setInterval(() => {
        this.moveRight();
      }, 100);
    } else if (posX <= window.innerWidth * 0.1 && !this.touchTimer) {
      this.moveLeft();
      this.touchTimer = setInterval(() => {
        this.moveLeft();
      }, 100);
    }
  };

  handleTouchEnd = () => {
    if (this.state.isEnergy) {
      return;
    }

    if (this.touchTimer) {
      clearInterval(this.touchTimer);
      this.touchTimer = null;
    }
  };

  moveRight() {
    this.state.symbols.forEach((mmb) => {
      if (
        this.state[mmb] &&
        this.state[mmb].mmb &&
        this.state[mmb].idx < this.state[mmb].mmb.length - 1
      ) {
        this.state[mmb].idx++;
      }
    });
    this.getImagesToDisplay();
    this.setState(this.state);
  }

  moveLeft() {
    this.state.symbols.forEach((mmb) => {
      if (this.state[mmb] && this.state[mmb].mmb && this.state[mmb].idx > 0) {
        this.state[mmb].idx--;
      }
    });
    this.getImagesToDisplay();
    this.setState(this.state);
  }

  getHistoryUrl(mmb, imgId) {
    const { year, month, day } = convertDateToEST(this.state.selectedDate);
    const symbolParam = this.state.isEnergy
      ? `symbol=${this.state.selectedEnergySymbol || "SPY"}`
      : "";
    const query = symbolParam ? `?${symbolParam}` : "";
    if (imgId) {
      return `${this.state.url}/${mmb}/${year}/${month}/${day}/${imgId}${query}`;
    }
    return `${this.state.url}/${mmb}/${year}/${month}/${day}${query}`;
  }

  // A workaround to access local images via public internet. This requires to pass
  // ngrok headers. Shouldn't be run when in local network.
  getImagesToDisplay() {
    if (process.env.REACT_APP_ENV !== "prod-ngrok") {
      return;
    }

    const promises = [];
    for (var symbol of this.state.symbols) {
      if (this.state[symbol] && this.state[symbol].mmb.length > 0) {
        const imgId = this.state[symbol].mmb[this.state[symbol].idx];
        const mmbUrl = this.getHistoryUrl(symbol, imgId);
        promises.push(
          axios({
            method: "get",
            url: mmbUrl,
            headers: getHeaders(),
            responseType: "arraybuffer",
            withCredentials: true,
          })
        );
      }
    }

    Promise.all(promises).then((results) => {
      const imgToDisplay = [];
      for (var res of results) {
        imgToDisplay.push(bufferToBase64Image(res.data));
      }
      this.state.imgToDisplay = imgToDisplay;
      this.setState(this.state);
    });
  }

  resizeMMB() {
    var imgId;
    var mmb;
    for (var symbol of this.state.symbols) {
      if (this.state[symbol].mmb.length > 0) {
        mmb = symbol;
        imgId = this.state[symbol].mmb[this.state[symbol].idx];
        break;
      }
    }

    if (!imgId) {
      return;
    }

    // The displayed size is always derived from the intrinsic size, so repeated
    // resizes cannot drift away from the real aspect ratio.
    if (this.naturalWidth && this.naturalHeight) {
      this.fit.reset();
      this.applyFit();
      return;
    }

    {
      const img = new Image();
      img.onload = () => {
        this.naturalWidth = img.naturalWidth;
        this.naturalHeight = img.naturalHeight;
        this.fit.reset();
        this.applyFit();
      };
      if (process.env.REACT_APP_ENV === "prod-ngrok") {
        axios({
          method: "get",
          url: this.getHistoryUrl(mmb, imgId),
          headers: getHeaders(),
          responseType: "arraybuffer",
          withCredentials: true,
        }).then((res) => {
          const imageInBase64 = window.btoa(String.fromCharCode(...new Uint8Array(res.data)));
          img.src = `data:image/png;base64,${imageInBase64}`;
        });
      } else {
        img.src = this.getHistoryUrl(mmb, imgId);
      }
    }
  }

  applyFit() {
    if (this.isUnmounted || !this.naturalWidth || !this.naturalHeight) {
      return;
    }

    const ratio = this.calculateRatio(this.naturalWidth, this.naturalHeight);
    this.state.mmbWidth = ratio * this.naturalWidth;
    this.state.mmbHeight = ratio * this.naturalHeight;
    this.fit.markFitted();
    this.setState(this.state);
  }

  getFitSignature() {
    return `${window.innerWidth}x${window.innerHeight}:${this.getHeaderHeight()}:${this.getContentTop()}:${this.getImageCount()}`;
  }

  getHeaderHeight() {
    return measureHeight(this.headerRef, FALLBACK_HEADER_HEIGHT);
  }

  getContentTop() {
    return measureTop(this.containerRef);
  }

  // The charts are stacked, so they share the height left below the header.
  getImageCount() {
    const withImages = this.state.symbols.filter(
      (symbol) => this.state[symbol] && this.state[symbol].mmb && this.state[symbol].mmb.length > 0
    );
    return Math.max(withImages.length, 1);
  }

  getAdjustedWindowSize() {
    const available =
      (window.innerHeight - this.getContentTop() - this.getHeaderHeight()) / this.getImageCount();
    return {
      width: window.innerWidth,
      height: Math.max(available, MIN_IMAGE_HEIGHT),
    };
  }

  calculateRatio(imgWidth, imgHeight) {
    const windowSize = this.getAdjustedWindowSize();
    var widthRatio = windowSize.width / imgWidth;
    var heightRatio = windowSize.height / imgHeight;
    return Math.min(widthRatio, heightRatio);
  }

  crawlMMBInternal(mmb, reset) {
    return axios({
      method: "get",
      url: this.getHistoryUrl(mmb),
      headers: getHeaders(),
      withCredentials: true,
    })
      .then((res) => {
        if (!this.state[mmb] || reset) {
          this.state[mmb] = {
            mmb: res.data,
          };
        } else {
          this.state[mmb].mmb = res.data;
        }

        if (res.data.length == 0) {
          this.state[mmb].err = `No ${mmb} found`;
        } else if (!this.state[mmb].idx || reset) {
          this.state[mmb].idx = 0;
        }
      })
      .catch((err) => {
        this.state[mmb] = {
          mmb: [],
        };
        if (err.response) {
          this.state[mmb].err = `${mmb}: ${err.response.data}`;
        }
      });
  }

  crawlMMB(reset) {
    if (this.state.isEnergy) {
      return;
    }

    const promises = [];
    for (var symbol of this.state.symbols) {
      promises.push(this.crawlMMBInternal(symbol, reset));
    }

    return Promise.all(promises).then(() => {
      this.resizeMMB();
      this.getImagesToDisplay();
      this.setState(this.state);
    });
  }

  crawlEnergyTableviewInternal(mmb) {
    return axios({
      method: "get",
      url: this.getHistoryUrl(mmb),
      headers: getHeaders(),
      withCredentials: true,
    })
      .then((res) => {
        this.state[mmb] = { tableview: res.data };
      })
      .catch((err) => {
        this.state[mmb] = { tableview: [] };
        if (err.response) {
          this.state[mmb].err = `${mmb}: ${err.response.data}`;
        }
      });
  }

  crawlEnergyTableview() {
    const promises = [];
    for (var symbol of this.state.symbols) {
      promises.push(this.crawlEnergyTableviewInternal(symbol));
    }

    return Promise.all(promises).then(() => {
      this.setState(this.state);
    });
  }

  onDateChangeInternal(date) {
    this.state.selectedDate = date;
    if (this.state.isEnergy) {
      this.crawlEnergyTableview();
    } else {
      // Another day can fall back to a different symbol's chart, which may not
      // share the intrinsic dimensions of the one measured so far.
      this.naturalWidth = null;
      this.naturalHeight = null;
      this.crawlMMB(true /* reset */);
    }
  }

  onDateChange(date) {
    if (this.state.onDateChangeCallback) {
      this.state.onDateChangeCallback(date);
    } else {
      this.onDateChangeInternal(date);
    }
  }

  onEnergySymbolChange() {
    if (!this.state.isEnergy || this.state.pinnedEnergySymbol) {
      return;
    }

    var symbol;
    if (this.state.selectedEnergySymbol === ENERGY_SYMBOL.SPY) {
      symbol = ENERGY_SYMBOL.QQQ;
    } else {
      symbol = ENERGY_SYMBOL.SPY;
    }

    this.state.selectedEnergySymbol = symbol;
    this.crawlEnergyTableview();
    this.setState(this.state);
  }

  showDatePicker() {
    if (this.state.isLive) {
      return;
    }

    const { selectedDate, marketCalendar } = this.state;
    const dateInEST = convertDateToESTString(selectedDate);

    return (
      <div className="inline">
        <h2 className="inline"> Date:</h2>
        <div className="inline">
          <DatePicker
            className="date-picker"
            selected={new Date(selectedDate.getTime() + getTimezoneDiffWithEST())}
            value={dateInEST}
            onChange={(date) => this.onDateChange(date)}
            filterDate={(date) => {
              const dInEST = new Date(date.getTime() - getTimezoneDiffWithEST());
              return isMarketOpenOnDate(marketCalendar, dInEST);
            }}
          />
        </div>
        <h2 className="inline">
          (EST) {dateInEST}
        </h2>
      </div>
    );
  }

  renderImages(mmbUrls) {
    const options = [];
    if (process.env.REACT_APP_ENV === "prod-ngrok") {
      for (var imageUri of this.state.imgToDisplay) {
        options.push(
          <EnlargeableImage
            width={this.state.mmbWidth}
            height={this.state.mmbHeight}
            src={imageUri}
          />
        );
      }
    } else {
      for (var url of mmbUrls) {
        options.push(
          <EnlargeableImage width={this.state.mmbWidth} height={this.state.mmbHeight} src={url} />
        );
      }
    }
    return options;
  }

  renderCoreContents(mmbUrls, columnData) {
    if (this.state.isEnergy) {
      const table = document.getElementById(this.tableId);
      const scrollToBottom = table
        ? table.scrollHeight - table.clientHeight <= table.scrollTop + 1
        : false;
      return (
        <Table
          columns={ENERGY_COLUMNS}
          data={columnData}
          scrollToBottom={scrollToBottom}
          tableId={this.tableId}
        />
      );
    } else {
      return <div className="core-content-container">{this.renderImages(mmbUrls)}</div>;
    }
  }

  render() {
    const mmbUrls = [];
    var errMsg = "";
    var tableviewData = [];
    for (var symbol of this.state.symbols) {
      if (!this.state.isEnergy) {
        if (this.state[symbol] && this.state[symbol].mmb.length > 0) {
          const imgId = this.state[symbol].mmb[this.state[symbol].idx];
          mmbUrls.push(this.getHistoryUrl(symbol, imgId));
        }
      } else {
        const tableview = this.state[symbol] ? this.state[symbol].tableview : [];
        tableviewData = tableview.map((e) => {
          const time = new Date(e.timestamp);
          return {
            timestamp: time.toLocaleString("en-US", formatTableViewOptions),
            falpha: e.energy.falpha,
            alpha: e.energy.alpha,
            beta: e.energy.beta,
            sd: e.energy.sd,
            bullalgos: e.energy.bullalgos,
            bearalgos: e.energy.bearalgos,
            battleprogram: e.energy.battleprogram,
            darkpools: e.energy.darkpools,
          };
        });
      }

      if (this.state[symbol] && this.state[symbol].err) {
        errMsg = errMsg.concat(" ").concat(this.state[symbol].err);
      }
    }

    return (
      <div className="component-app" ref={this.containerRef}>
        <div className="header-bar" ref={this.headerRef}>
          {this.showDatePicker()}
          {this.state.isLive && (
            <h2 className="inline">
              {convertDateToESTString(this.state.selectedDate)}
            </h2>
          )}
          {this.state.isEnergy &&
            (this.state.pinnedEnergySymbol ? (
              <h2 className="inline">
                <b>{this.state.selectedEnergySymbol}</b>
              </h2>
            ) : (
              <button
                className="inline"
                onClick={() => this.onEnergySymbolChange()}
              >
                {this.state.selectedEnergySymbol}
              </button>
            ))}
          {errMsg && (
            <h2 className="inline">
              <b className="latest">{errMsg}</b>
            </h2>
          )}
        </div>
        {this.renderCoreContents(mmbUrls, tableviewData)}
      </div>
    );
  }
}
